import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { FileCheck2, PlugZap, Save, Unplug } from 'lucide-react';
import { toast } from 'sonner';
import { einvoiceApi } from '../services/api';

export const EinvoiceSettingsCard = () => {
  const [s, setS] = useState(null);
  const [form, setForm] = useState({ aruba_username: '', aruba_password: '', environment: 'demo', start_number: 1, start_year: new Date().getFullYear(), natura_iva_zero: 'N4', default_payment_method: 'MP05' });
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = (data) => {
    setS(data);
    setForm((f) => ({ ...f, ...data, aruba_password: '' }));
  };

  useEffect(() => { einvoiceApi.getSettings().then((r) => load(r.data)).catch(() => {}); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form, start_number: parseInt(form.start_number) || 1, start_year: parseInt(form.start_year) };
      if (!payload.aruba_password) delete payload.aruba_password;
      const r = await einvoiceApi.updateSettings(payload);
      load(r.data);
      toast.success('Impostazioni fatturazione salvate');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore nel salvataggio'); }
    finally { setBusy(false); }
  };

  const disconnect = async () => {
    setConfirmOpen(false);
    setBusy(true);
    try {
      const r = await einvoiceApi.updateSettings({ disconnect: true });
      load(r.data);
      toast.success('Account Aruba scollegato');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setBusy(false); }
  };

  const test = async () => {
    setBusy(true);
    try {
      await einvoiceApi.testConnection();
      toast.success('Collegamento ad Aruba riuscito');
    } catch (e) { toast.error(e.response?.data?.detail || 'Collegamento non riuscito'); }
    finally { setBusy(false); }
  };

  if (!s) return null;
  const set = (k, v) => setForm({ ...form, [k]: v });

  return (
    <Card data-testid="einvoice-settings-card" className="card-swiss">
      <CardHeader>
        <CardTitle className="text-base font-semibold font-['Manrope'] flex items-center gap-2">
          <FileCheck2 className="w-4 h-4" /> Fatturazione elettronica (Aruba)
          {s.configured
            ? <Badge data-testid="einvoice-configured-badge" className="bg-emerald-100 text-emerald-800 border-emerald-300">Collegato</Badge>
            : <Badge variant="outline" className="border-amber-300 text-amber-700">Non collegato</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">Inserisci le credenziali del tuo servizio Aruba Fatturazione Elettronica. La password viene salvata cifrata e non viene più mostrata.</p>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="label-uppercase">Utente Aruba</Label>
            <Input data-testid="aruba-username-input" value={form.aruba_username} onChange={(e) => set('aruba_username', e.target.value)} autoComplete="off" />
          </div>
          <div className="space-y-2">
            <Label className="label-uppercase">Password Aruba</Label>
            <Input data-testid="aruba-password-input" type="password" value={form.aruba_password} onChange={(e) => set('aruba_password', e.target.value)}
              placeholder={s.configured ? '•••••••• (lascia vuoto per non cambiarla)' : ''} autoComplete="new-password" />
          </div>
          <div className="space-y-2">
            <Label className="label-uppercase">Ambiente</Label>
            <Select value={form.environment} onValueChange={(v) => set('environment', v)}>
              <SelectTrigger data-testid="aruba-environment-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="demo">Prova (demo Aruba)</SelectItem>
                <SelectItem value="production">Reale (invio allo SDI)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid md:grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label className="label-uppercase">Numero di partenza</Label>
            <Input data-testid="einvoice-start-number-input" type="number" min="1" value={form.start_number} onChange={(e) => set('start_number', e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label className="label-uppercase">Per l'anno</Label>
            <Input data-testid="einvoice-start-year-input" type="number" value={form.start_year} onChange={(e) => set('start_year', e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label className="label-uppercase">Pagamento predefinito</Label>
            <Select value={form.default_payment_method} onValueChange={(v) => set('default_payment_method', v)}>
              <SelectTrigger data-testid="einvoice-default-payment-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(s.payment_methods).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="label-uppercase">Prodotti con IVA 0%</Label>
            <Select value={form.natura_iva_zero} onValueChange={(v) => set('natura_iva_zero', v)}>
              <SelectTrigger data-testid="einvoice-natura-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(s.nature).map(([k, v]) => <SelectItem key={k} value={k}>{k} · {v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <p data-testid="einvoice-next-number" className="text-xs text-muted-foreground">Prossima fattura: <b>N. {s.next_number}</b> · Regime fiscale: Ordinario (RF01)</p>
        <div className="flex gap-2 flex-wrap">
          <Button data-testid="save-einvoice-settings-btn" type="button" onClick={save} disabled={busy} className="btn-primary">
            <Save className="w-4 h-4 mr-2" /> Salva
          </Button>
          <Button data-testid="test-aruba-connection-btn" type="button" variant="outline" onClick={test} disabled={busy || !s.configured}>
            <PlugZap className="w-4 h-4 mr-2" /> Prova collegamento
          </Button>
          {s.configured && (
            <Button data-testid="disconnect-aruba-btn" type="button" variant="ghost" className="text-destructive" onClick={() => setConfirmOpen(true)} disabled={busy}>
              <Unplug className="w-4 h-4 mr-2" /> Scollega
            </Button>
          )}
        </div>
        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Scollegare l'account Aruba?</AlertDialogTitle>
              <AlertDialogDescription>Non potrai più inviare fatture allo SDI finché non lo ricolleghi.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annulla</AlertDialogCancel>
              <AlertDialogAction data-testid="confirm-disconnect-aruba-btn" onClick={disconnect} className="bg-destructive hover:bg-destructive/90">Scollega</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
};

export default EinvoiceSettingsCard;
